/**
 * DC cable helpers for inverter-level DC cabling.
 *
 *   - applyRefreshCableLengthDefaults: seed missing cable lengths on string
 *     DC cable slots when the user hits "Auto-select for this inverter".
 *   - getInverterDCCableSegments / getTotalDCCableLengthForInverter: collect
 *     all DC cable segments contributing to the shared inverter cable SKU,
 *     and sum their lengths for purchase-quantity calculations.
 *   - getDCCablePurchaseQty: how many reels of this SKU you need.
 *   - getDCCableVoltageDropSummary: aggregate pass/fail across every segment
 *     for the inverter-level info strip.
 */
import { StudioInverterType, StudioSystemType } from 'types/global'
import { SlotType } from 'types/slots'
import { getSlotsByLocation, updateSlot } from 'projectSections/sections/design/systems/Slots'
import {
  getConfiguredOrDefaultDCCableLengthMeters,
  getDefaultDCCableLengthMeters,
} from './dcCableLengthDefaults'
import { computeDCCablePurchasePlan, STANDARD_DC_REEL_LENGTH_OPTIONS_METERS } from './dcCableReelLength'
import { getCableVoltageDrop, getDCStringVoltage, getMaxCableCurrent } from './helpers'

export type DCCableSegment = {
  label: string
  length: number
  location: any
}

export type DCCableVoltageDropSummary = {
  status: 'success' | 'marginal' | 'failure' | null
  message: string | null
}

export function applyRefreshCableLengthDefaults(inverter: StudioInverterType, system: StudioSystemType) {
  if (!inverter || inverter.microinverter) return

  const ensureCableLength = (slot: SlotType, defaultLengthMeters: number) => {
    const existingLength = Number(slot?.properties?.cableLength)
    const hasValidLength = Number.isFinite(existingLength) && existingLength > 0
    if (hasValidLength) return
    if (!slot.properties) slot.properties = {}
    slot.properties.cableLength = defaultLengthMeters
    updateSlot(slot, system)
  }

  inverter.mppts().forEach((mppt: any) => {
    mppt.strings().forEach((str: any) => {
      getSlotsByLocation(str.uuid, system).forEach((slot: SlotType) => {
        if (slot.type === 'dc_cable') {
          ensureCableLength(slot, getDefaultDCCableLengthMeters(str))
        }
      })
    })
  })
}

export function getDCCableLengthFromSlot(
  slot?: SlotType,
  attachObject?: { type?: string; moduleQuantity?: () => number; modules?: unknown[] }
): number {
  return getConfiguredOrDefaultDCCableLengthMeters(slot?.properties?.cableLength, attachObject)
}

export function getInverterDCCableSegments(system: StudioSystemType, inverter: any): DCCableSegment[] {
  const slots = system.userData?.slots || []
  const segments: DCCableSegment[] = []
  inverter.mppts().forEach((mppt: any, mpptIndex: number) => {
    const mpptSlot = slots.find((s: SlotType) => s.type === 'dc_cable' && s.attachToUuid === mppt.uuid)
    segments.push({
      label: `MPPT ${mpptIndex + 1}`,
      length: getDCCableLengthFromSlot(mpptSlot, mppt),
      location: mppt,
    })
    mppt.strings().forEach((str: any, strIndex: number) => {
      const strSlot = slots.find((s: SlotType) => s.type === 'dc_cable' && s.attachToUuid === str.uuid)
      segments.push({
        label: `MPPT ${mpptIndex + 1} · String ${strIndex + 1}`,
        length: getDCCableLengthFromSlot(strSlot, str),
        location: str,
      })
    })
  })
  return segments
}

export function getTotalDCCableLengthForInverter(system: StudioSystemType, inverter: any): number {
  return getInverterDCCableSegments(system, inverter).reduce((sum, segment) => sum + segment.length, 0)
}

export function getDCCablePurchaseQuantity(
  cable: { cable_length?: number | null },
  system: StudioSystemType,
  inverter: any,
  reelLengthOptions: number[] = STANDARD_DC_REEL_LENGTH_OPTIONS_METERS
): number {
  const reelLength = Number(cable?.cable_length)
  if (!Number.isFinite(reelLength) || reelLength <= 0) return 1
  const totalLength = getTotalDCCableLengthForInverter(system, inverter)
  if (totalLength <= 0) return 1
  const plan = computeDCCablePurchasePlan(totalLength, reelLengthOptions)
  if (plan.reelLength === reelLength) return plan.quantity
  return Math.max(1, Math.ceil(totalLength / reelLength))
}

export function getDCCablePurchaseQty(cable: any, system: StudioSystemType, inverter: any): string {
  if (!cable?.cable_length || cable.cable_length <= 0) return '—'
  return String(getDCCablePurchaseQuantity(cable, system, inverter))
}

export function getDCCableVoltageDropSummary(cable: any, segments: DCCableSegment[]): DCCableVoltageDropSummary {
  if (!cable || segments.length === 0) return { status: null, message: null }

  const failingSegments: string[] = []
  const marginalSegments: string[] = []
  const errorMessages: string[] = []

  segments.forEach((segment) => {
    const current = getMaxCableCurrent(segment.location)
    const dcVoltage = getDCStringVoltage(segment.location)
    const result = getCableVoltageDrop(cable, segment.length, 'single_phase', current, dcVoltage)
    if (result.data?.error) {
      errorMessages.push(result.data.error)
    } else if (result.compatibility === 'bad') {
      failingSegments.push(segment.label)
    } else if (result.compatibility === 'ok') {
      marginalSegments.push(segment.label)
    }
  })

  if (errorMessages.length > 0) {
    return {
      status: 'failure',
      message: errorMessages[0],
    }
  }

  if (failingSegments.length > 0) {
    return {
      status: 'failure',
      message:
        failingSegments.length === 1
          ? `Voltage drop check failed for ${failingSegments[0]}.`
          : `Voltage drop checks failed for ${failingSegments.length} sections on this inverter.`,
    }
  }

  if (marginalSegments.length > 0) {
    return {
      status: 'marginal',
      message:
        marginalSegments.length === 1
          ? `Voltage drop on ${marginalSegments[0]} is within limit but has low margin — consider upsizing.`
          : `${marginalSegments.length} sections pass voltage drop with low margin — consider upsizing.`,
    }
  }

  return {
    status: 'success',
    message: 'Voltage drop checks pass for all strings on this inverter.',
  }
}
